import type { Active, UniqueIdentifier } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import './SortableList.css';
import { SortableOverlay } from './SortableOverlay';
import SortableItem, { StaticItem } from './SortableItem';

// Default auto-scroll walks every scrollable ancestor on a ~5ms interval, so the loop can reflow the
// document on every tick. In the POS WebView the document is a live candidate: the native host
// overwrites this app's viewport meta at load time with `initial-scale=0.9, maximum-scale=0.9`
// (ontiloo-pos-app BookingScreen/Schedule.tsx), laying out wider than the device. Only an element
// that opts in may scroll.
const AUTO_SCROLL = {
  canScroll: (el: Element) => el instanceof HTMLElement && el.dataset.dndScroll === 'true',
};

interface BaseItem {
  id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
  items: T[];
  onChange(items: T[]): void;
  // Returns row content only. This component owns the sortable wrapper so a caller cannot leak a
  // second useSortable registration into the drag overlay.
  renderItem(item: T): ReactNode;
  useGrid?: boolean;
}

export function SortableList<T extends BaseItem>({
  items,
  onChange,
  renderItem,
  useGrid
}: Props<T>) {
  const [active, setActive] = useState<Active | null>(null);
  const activeItem = useMemo(
    () => items.find((item) => item.id === active?.id),
    [active, items]
  );
  // One pointer stream covers mouse and touch. Android WebView also emits compatibility mouse
  // events after a touch sequence, so separate Mouse/Touch sensors contend over one gesture.
  const pointerSensor = useSensor(PointerSensor, {
    // Require the pointer to move by 8 pixels before activating
    activationConstraint: {
      distance: 8,
    },
  });

  const sensors = useSensors(pointerSensor);

  return (
    <DndContext
      sensors={sensors}
      autoScroll={AUTO_SCROLL}
      onDragStart={({ active }) => {
        setActive(active);
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);

          onChange(arrayMove(items, activeIndex, overIndex));
        }
        setActive(null);
      }}
      onDragCancel={() => {
        setActive(null);
      }}
    >
      <SortableContext items={items} strategy={useGrid ? undefined : verticalListSortingStrategy}>
        {useGrid ? <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {items.map((item) => (
            <SortableItem key={item.id} id={item.id}>{renderItem(item)}</SortableItem>
          ))}
        </div> :
          <ul className="SortableList" role="application">
            {items.map((item) => (
              <SortableItem key={item.id} id={item.id}>{renderItem(item)}</SortableItem>
            ))}
          </ul>
        }
      </SortableContext>
      <SortableOverlay>
        {activeItem ? <StaticItem>{renderItem(activeItem)}</StaticItem> : null}
      </SortableOverlay>
    </DndContext >
  );
}
